import { useState, useEffect, useCallback } from "react";
import { P, FF, inputBase } from "../../styles/tokens";
import { Card } from "../ui";
import { api } from "../../services/api";
import { useNavigation } from "../../contexts/NavigationContext";
import { useToast } from "../../contexts/ToastContext";
import { PLAN_LABELS } from "../../constants/planLimits";

const PLAN_OPTIONS = ["gratis", "basico", "pro"];

const PLAN_BADGE_STYLE = {
  gratis:  { background: P.bg,           color: P.textSm,        border: `1px solid ${P.border}` },
  basico:  { background: P.fijos.pastel, color: P.fijos.text,    border: `1px solid ${P.fijos.tint}` },
  pro:     { background: "#FEF9EC",      color: "#7A4E06",        border: "1px solid #FDE8B0" },
};

function PlanBadge({ plan }) {
  const s = PLAN_BADGE_STYLE[plan] ?? PLAN_BADGE_STYLE.gratis;
  return (
    <span style={{
      ...s,
      borderRadius: 8, padding: "2px 8px",
      fontFamily: FF, fontSize: 11, fontWeight: 700,
    }}>
      {PLAN_LABELS[plan] ?? plan}
    </span>
  );
}

/* ════════════════════════════════════════════════════════════
   UserRow — fila individual con controles de asignación
   ════════════════════════════════════════════════════════════ */
function UserRow({ user, onAssigned }) {
  const { show: showToast } = useToast();
  const [newPlan, setNewPlan] = useState(user.plan ?? "gratis");
  const [periodEnd, setPeriodEnd] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleAssign = async () => {
    setConfirmOpen(false);
    setSaving(true);
    try {
      await api.admin.assignPlan(user.user_id, newPlan, periodEnd || undefined);
      showToast(`Plan de ${user.email} actualizado a ${PLAN_LABELS[newPlan] ?? newPlan}`);
      onAssigned();
    } catch (e) {
      showToast("Error al asignar plan: " + (e.message || "intenta de nuevo"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card style={{ padding: "12px 14px" }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
        {/* Email + plan actual */}
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: P.text, fontFamily: FF, marginBottom: 3 }}>
            {user.email}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 11, color: P.textSm, fontFamily: FF }}>Plan actual:</span>
            <PlanBadge plan={user.plan ?? "gratis"} />
          </div>
          {user.current_period_end && (
            <div style={{ fontSize: 11, color: P.textXs, fontFamily: FF, marginTop: 2 }}>
              Hasta: {new Date(user.current_period_end).toLocaleDateString("es-MX")}
            </div>
          )}
        </div>

        {/* Controles de asignación */}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          <select
            value={newPlan}
            onChange={(e) => setNewPlan(e.target.value)}
            style={{ ...inputBase, width: "auto", padding: "6px 10px", fontSize: 12, appearance: "auto" }}
          >
            {PLAN_OPTIONS.map((p) => (
              <option key={p} value={p}>{PLAN_LABELS[p]}</option>
            ))}
          </select>

          <div style={{ display: "flex", flexDirection: "column" }}>
            <label style={{ fontSize: 10, fontWeight: 700, color: P.textXs, fontFamily: FF, textTransform: "uppercase", marginBottom: 2 }}>
              Hasta (opcional)
            </label>
            <input
              type="date"
              value={periodEnd}
              onChange={(e) => setPeriodEnd(e.target.value)}
              style={{ ...inputBase, width: 140, padding: "6px 10px", fontSize: 12 }}
            />
          </div>

          {confirmOpen ? (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 12, color: P.textSm, fontFamily: FF }}>¿Confirmar?</span>
              <button
                onClick={handleAssign}
                disabled={saving}
                style={{
                  padding: "6px 12px", borderRadius: 8, border: "none",
                  background: saving ? P.border : P.success.base,
                  color: saving ? P.textSm : "#fff",
                  fontFamily: FF, fontSize: 12, fontWeight: 700,
                  cursor: saving ? "default" : "pointer",
                }}
              >
                {saving ? "..." : "Sí"}
              </button>
              <button
                onClick={() => setConfirmOpen(false)}
                style={{
                  padding: "6px 10px", borderRadius: 8,
                  border: `1.5px solid ${P.border}`, background: "transparent",
                  color: P.textSm, fontFamily: FF, fontSize: 12, fontWeight: 600, cursor: "pointer",
                }}
              >
                No
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirmOpen(true)}
              style={{
                padding: "7px 14px", borderRadius: 8, border: "none",
                background: P.ingresos.base, color: "#fff",
                fontFamily: FF, fontSize: 12, fontWeight: 700, cursor: "pointer",
              }}
            >
              Asignar
            </button>
          )}
        </div>
      </div>
    </Card>
  );
}

/* ════════════════════════════════════════════════════════════
   AdminPanel — Panel principal
   ════════════════════════════════════════════════════════════ */
const PAGE_LIMIT = 50;

export default function AdminPanel() {
  const { setView } = useNavigation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const fetchUsers = useCallback(async (off = 0) => {
    setLoading(true);
    try {
      const res = await api.admin.listUsers({ limit: PAGE_LIMIT, offset: off });
      // res puede ser array directamente o { users, total }
      const list = Array.isArray(res) ? res : (res?.users ?? []);
      setUsers(list);
      setHasMore(list.length === PAGE_LIMIT);
      setOffset(off);
      setForbidden(false);
    } catch (e) {
      if (e.message?.includes("403")) {
        setForbidden(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(0); }, [fetchUsers]);

  const filtered = users.filter((u) =>
    !search.trim() || u.email?.toLowerCase().includes(search.toLowerCase())
  );

  /* ── Acceso restringido ── */
  if (forbidden) return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <button
        onClick={() => setView("config")}
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "8px 14px", borderRadius: 10, border: `1.5px solid ${P.border}`,
          background: P.bg, color: P.textSm, cursor: "pointer",
          fontFamily: FF, fontSize: 12, fontWeight: 600, alignSelf: "flex-start",
        }}
      >
        ← Volver a Config
      </button>
      <Card style={{ padding: "24px 22px", textAlign: "center" }}>
        <div style={{ fontSize: 32, marginBottom: 10 }}>🔒</div>
        <div style={{ fontSize: 15, fontWeight: 800, color: P.text, fontFamily: FF, marginBottom: 6 }}>
          Acceso restringido
        </div>
        <div style={{ fontSize: 13, color: P.textSm, fontFamily: FF }}>
          No eres admin. Esta sección no está disponible para tu cuenta.
        </div>
      </Card>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* ── Header ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <button
          onClick={() => setView("config")}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "8px 14px", borderRadius: 10, border: `1.5px solid ${P.border}`,
            background: P.bg, color: P.textSm, cursor: "pointer",
            fontFamily: FF, fontSize: 12, fontWeight: 600,
          }}
        >
          ← Volver a Config
        </button>
        <div style={{ fontSize: 17, fontWeight: 900, color: P.text, fontFamily: FF }}>
          Panel Admin
        </div>
      </div>

      {/* ── Buscador ── */}
      <input
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Buscar por email..."
        style={{ ...inputBase }}
      />

      {/* ── Lista ── */}
      {loading ? (
        <Card style={{ padding: "24px", textAlign: "center" }}>
          <div style={{ fontSize: 13, color: P.textSm, fontFamily: FF }}>Cargando usuarios...</div>
        </Card>
      ) : filtered.length === 0 ? (
        <Card style={{ padding: "24px", textAlign: "center" }}>
          <div style={{ fontSize: 13, color: P.textSm, fontFamily: FF }}>
            {search ? "Sin resultados para esa búsqueda." : "No hay usuarios."}
          </div>
        </Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ fontSize: 11, color: P.textXs, fontFamily: FF, fontWeight: 600 }}>
            {filtered.length} usuario{filtered.length !== 1 ? "s" : ""}
            {search ? " (filtrado)" : ""}
          </div>
          {filtered.map((u) => (
            <UserRow key={u.user_id} user={u} onAssigned={() => fetchUsers(offset)} />
          ))}
        </div>
      )}

      {/* ── Paginación ── */}
      {!search && (
        <div style={{ display: "flex", gap: 8 }}>
          {offset > 0 && (
            <button
              onClick={() => fetchUsers(Math.max(0, offset - PAGE_LIMIT))}
              disabled={loading}
              style={{
                padding: "8px 16px", borderRadius: 10,
                border: `1.5px solid ${P.border}`, background: P.bg,
                color: P.textSm, fontFamily: FF, fontSize: 12, fontWeight: 600,
                cursor: loading ? "default" : "pointer",
              }}
            >
              ← Anterior
            </button>
          )}
          {hasMore && (
            <button
              onClick={() => fetchUsers(offset + PAGE_LIMIT)}
              disabled={loading}
              style={{
                padding: "8px 16px", borderRadius: 10,
                border: `1.5px solid ${P.border}`, background: P.bg,
                color: P.textSm, fontFamily: FF, fontSize: 12, fontWeight: 600,
                cursor: loading ? "default" : "pointer",
              }}
            >
              Siguiente →
            </button>
          )}
        </div>
      )}
    </div>
  );
}
